import * as React from "react";
import styled from "@mui/material/styles/styled";
import { GenderValue } from "shared";
import { UndoQuestion } from "../uiControllers/undoQuestion";
import { useTranslate } from "../../hooks/useTranslate";
import { SymptomIconObject } from "../Questions/ClosedSingleQuestion";
import { useSymptomIcon } from "../../hooks/useSymptomIcon";
import { usePatient } from "../../state/patientContext";
import { TextAnswer } from "./textAnswer";
import { AnswerElementExchange } from "./AnswerElementExchange";
import { getAnswerTextByType } from "./getAnswerTextByType";
import clsx from "clsx";
import { ForwardedRef, forwardRef } from "react";

type AnswerElementProps = {
  gender?: GenderValue;
  onUndo: () => void;
  exchange: AnswerElementExchange;
};
//region [[ Styles ]]

const AnswerElementView = styled("div")({
  display: "flex",
  flexFlow: "row-reverse",
  minHeight: "45px",
});

export const AnswerContainer = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "flex-end",
  margin: "10px 0",
  flexWrap: "nowrap",
  "&.long-format": {
    alignItems: "flex-start",
    justifyContent: "flex-start",
    flexFlow: " wrap-reverse",
  },
});

const AnswerIcon = styled("div")({
  "& div svg": {
    height: "100px",
    width: "100px",
  },
  marginBottom: "10px",
});

const UndoContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  justifyContent: "center",
  width: "fit-content",
});
//endregion [[ Styles ]]

//region [[ Props ]]

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

export const AnswerElement = React.memo(
  forwardRef((props: AnswerElementProps, ref: ForwardedRef<HTMLDivElement>) => {
    const { exchange, onUndo, gender: _gender } = props;

    const { gender: stateGender } = usePatient();

    const gender = _gender || stateGender;

    const { translate } = useTranslate();

    const notSkippedFeatures = exchange.answer?.qfs.filter((s) => !s?.negate);

    const isSingleAnswer = notSkippedFeatures.length === 1;

    const qf = isSingleAnswer ? notSkippedFeatures?.[0] : null;

    const symptomIcon = useSymptomIcon(
      qf && !qf?.skip && !qf?.negate && !qf.qualifiers?.length
        ? qf?.feature?.id
        : null,
    );

    const visualAnswer = getAnswerTextByType(translate, exchange, gender);

    return (
      <AnswerElementView className={"answer-element"} ref={ref}>
        {symptomIcon && (
          <AnswerIcon>
            <SymptomIconObject>{symptomIcon}</SymptomIconObject>
          </AnswerIcon>
        )}
        <AnswerContainer
          className={clsx(
            "answer-container",
            visualAnswer.isLongFormat ? "long-format" : "",
            onUndo ? "tooltip-highlight" : "",
          )}
        >
          <UndoContainer>
            {onUndo && <UndoQuestion onUndo={props.onUndo} />}
            &nbsp;
          </UndoContainer>
          <TextAnswer
            className={"text-answer"}
            gender={gender}
            answerText={visualAnswer.text}
            longFormat={visualAnswer.isLongFormat}
            keepLtr={visualAnswer.keepLtr}
            isNumeric={visualAnswer.isNumeric}
          />
        </AnswerContainer>
      </AnswerElementView>
    );
  }),
);
